/**
 * Production-safe logging utility
 * 
 * In production builds:
 * - All console logs are automatically removed by esbuild
 * - This provides a consistent interface that's tree-shaken away
 * 
 * On localhost only:
 * - Full logging functionality with enhanced formatting
 */

declare global {
  const __PROD__: boolean;
}

// Only enable logging on localhost
const isLocalhost = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || 
   window.location.hostname === '127.0.0.1' || 
   window.location.hostname === '0.0.0.0');

const shouldLog = typeof __PROD__ !== 'undefined' ? !__PROD__ && isLocalhost : 
  process.env.NODE_ENV === 'development' && isLocalhost;

class Logger {
  private prefix = '[App]';

  log(...args: any[]) {
    if (shouldLog) {
      console.log(this.prefix, ...args);
    }
  }

  info(...args: any[]) {
    if (shouldLog) {
      console.info(this.prefix, '📋', ...args);
    }
  }

  warn(...args: any[]) {
    if (shouldLog) {
      console.warn(this.prefix, '⚠️', ...args);
    }
  }

  error(...args: any[]) {
    if (shouldLog) {
      console.error(this.prefix, '❌', ...args);
    }
  }

  debug(...args: any[]) {
    if (shouldLog) {
      console.debug(this.prefix, '🐛', ...args);
    }
  }

  success(...args: any[]) {
    if (shouldLog) {
      console.log(this.prefix, '✅', ...args);
    }
  }

  trace(...args: any[]) {
    if (shouldLog) {
      console.trace(this.prefix, '🔍', ...args);
    }
  }

  group(label: string) {
    if (shouldLog) {
      console.group(this.prefix, label);
    }
  }

  groupEnd() {
    if (shouldLog) {
      console.groupEnd();
    }
  }

  time(label: string) {
    if (shouldLog) {
      console.time(label);
    }
  }

  timeEnd(label: string) {
    if (shouldLog) {
      console.timeEnd(label);
    }
  }
}

// Export singleton instance
export const logger = new Logger();

// Export individual methods for convenience
export const { log, info, warn, error, debug, success, trace, group, groupEnd, time, timeEnd } = logger; 